import { useRef } from 'react';
import { useC3Chart } from '../../hooks/useC3Chart';
import type { ReviewStatistics } from '../../hooks/useReview';

interface Props {
  reviewStats: ReviewStatistics;
}

export function BurnDownChart({ reviewStats }: Props) {
  const chartRef = useRef<HTMLDivElement>(null);

  useC3Chart(chartRef, () => {
    const rawEntries = Object.entries(reviewStats.statistics).map(([dateKey, stats]) => ({
      date: new Date(dateKey).getTime(),
      ...stats,
    }));

    // Group by local calendar day
    const dayMap = new Map<number, typeof rawEntries>();
    for (const entry of rawEntries) {
      const d = new Date(entry.date);
      const dayKey = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
      const group = dayMap.get(dayKey) ?? [];
      group.push(entry);
      dayMap.set(dayKey, group);
    }

    const flattened = Array.from(dayMap.values())
      .map(group => {
        const last = group[group.length - 1];
        return {
          date: last.date,
          idealResourcesRemaining: last.idealResourcesRemaining,
          actualResourcesRemaining: last.actualResourcesRemaining,
          idealBurnRate: last.idealBurnRate,
          actualBurnRate: group.reduce((sum, e) => sum + e.actualBurnRate, 0),
        };
      })
      .sort((a, b) => a.date - b.date);

    if (!flattened.length) return null;

    const lastRecordedTs = new Date(reviewStats.lastRecordedDate).setHours(0, 0, 0, 0);
    const regionStart = flattened.find(s => s.date >= lastRecordedTs)?.date
      ?? flattened[flattened.length - 1].date;

    return {
      size: { height: 450 },
      data: {
        x: 'x',
        columns: [
          ['x', ...flattened.map(s => new Date(s.date))],
          ['Target Burn Down', ...flattened.map(s => s.idealResourcesRemaining)],
          ['Total Items Remaining', ...flattened.map(s => s.actualResourcesRemaining)],
          ['Daily Review Target', ...flattened.map(s => s.idealBurnRate)],
          ['Reviewed', ...flattened.map(s => s.actualBurnRate)],
        ],
        type: 'bar',
        types: {
          'Target Burn Down': 'line',
          'Total Items Remaining': 'line',
          'Daily Review Target': 'step',
          'Reviewed': 'bar',
        },
        regions: {
          'Total Items Remaining': [{ start: regionStart, style: 'dashed' }],
        },
      },
      transition: { duration: 0 },
      color: { pattern: ['#0099CC', '#FF8800', '#007E33', '#3F729B'] },
      axis: {
        x: {
          localtime: true,
          type: 'timeseries',
          tick: {
            format: '%Y-%m-%d',
            culling: { max: 50 },
            rotate: 50,
            multiline: false,
          },
        },
        y: {
          min: 0,
          padding: { bottom: 0 },
          tick: { format: (v: number) => v.toLocaleString() },
        },
      },
      grid: { x: { show: false }, y: { show: false } },
      zoom: { enabled: false },
      subchart: { show: false },
    };
  }, [reviewStats]);

  return <div ref={chartRef} />;
}
